export interface StoredColorSettings {
    brightnessLevel?: number;
    contrastLevel?: number;
    saturationLevel?: number;
    gamma?: number;
}

type StoredColorSettingsMap = Record<string, StoredColorSettings>;

export interface ColorSettingsContext {
    jobId?: number | null;
    frame?: number | null;
}

const STORAGE_KEY = 'cvat:playerColorSettings';
const DEFAULT_CONTEXT = 'global';

function isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function sanitizeSettings(values: StoredColorSettings): StoredColorSettings {
    return Object.fromEntries(
        Object.entries(values).filter(([, value]) => typeof value !== 'undefined' && value !== null),
    ) as StoredColorSettings;
}

function readStorage(): StoredColorSettingsMap {
    if (!isBrowser()) {
        return {};
    }

    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return {};
        }

        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
            if (
                'brightnessLevel' in parsed ||
                'contrastLevel' in parsed ||
                'saturationLevel' in parsed ||
                'gamma' in parsed
            ) {
                return { [DEFAULT_CONTEXT]: parsed as StoredColorSettings };
            }

            return parsed as StoredColorSettingsMap;
        }
    } catch {
        // ignore storage errors
    }

    return {};
}

function writeStorage(map: StoredColorSettingsMap): void {
    if (!isBrowser()) {
        return;
    }

    try {
        const entries = Object.entries(map).filter(([, value]) => Object.keys(value || {}).length);
        if (!entries.length) {
            window.localStorage.removeItem(STORAGE_KEY);
            return;
        }

        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch {
        // ignore storage errors
    }
}

function deriveContextFromLocation(): { jobKey?: string; taskKey?: string } {
    if (!isBrowser()) {
        return {};
    }

    const href = `${window.location.pathname}${window.location.hash}`;
    const jobMatch = href.match(/jobs\/(\d+)/);
    const taskMatch = href.match(/tasks\/(\d+)/);

    const result: { jobKey?: string; taskKey?: string } = {};
    if (jobMatch) {
        result.jobKey = `job:${jobMatch[1]}`;
    } else if (taskMatch) {
        result.taskKey = `task:${taskMatch[1]}`;
    }

    return result;
}

function buildContextKeys(context?: ColorSettingsContext): string[] {
    const keys: string[] = [];

    if (context?.jobId !== null && typeof context?.jobId !== 'undefined') {
        if (context?.frame !== null && typeof context?.frame !== 'undefined') {
            keys.push(`job:${context.jobId}:frame:${context.frame}`);
        }
        keys.push(`job:${context.jobId}`);
    }

    const fromLocation = deriveContextFromLocation();
    if (fromLocation.jobKey) {
        if (context?.frame !== null && typeof context?.frame !== 'undefined') {
            keys.push(`${fromLocation.jobKey}:frame:${context.frame}`);
        }
        keys.push(fromLocation.jobKey);
    }
    if (fromLocation.taskKey) {
        keys.push(fromLocation.taskKey);
    }

    keys.push(DEFAULT_CONTEXT);
    return [...new Set(keys)];
}

function getPrimaryKey(context?: ColorSettingsContext | number | null | undefined): string {
    if (typeof context === 'number') {
        return `job:${context}`;
    }

    if (typeof context === 'object' && context !== null) {
        if (context.jobId !== null && typeof context.jobId !== 'undefined') {
            if (context.frame !== null && typeof context.frame !== 'undefined') {
                return `job:${context.jobId}:frame:${context.frame}`;
            }
            return `job:${context.jobId}`;
        }
    }

    const fromLocation = deriveContextFromLocation();
    if (fromLocation.jobKey) {
        if (typeof context === 'object' && context?.frame !== null && typeof context?.frame !== 'undefined') {
            return `${fromLocation.jobKey}:frame:${context.frame}`;
        }
        return fromLocation.jobKey;
    }
    if (fromLocation.taskKey) {
        return fromLocation.taskKey;
    }

    return DEFAULT_CONTEXT;
}

export function getStoredColorSettings(context?: ColorSettingsContext | number | null): StoredColorSettings {
    const storage = readStorage();
    const contextObj: ColorSettingsContext = typeof context === 'number' ? { jobId: context } :
        (typeof context === 'object' && context !== null ? context : {});
    const keys = buildContextKeys(contextObj);
    for (const key of keys) {
        if (storage[key]) {
            return storage[key];
        }
    }

    return {};
}

export function updateStoredColorSettings(
    context: ColorSettingsContext | number | null | undefined,
    values: Partial<StoredColorSettings>,
): void {
    const map = readStorage();
    const contextKey = getPrimaryKey(context);
    const current = map[contextKey] || {};
    const next = sanitizeSettings({
        ...current,
        ...values,
    });

    if (Object.keys(next).length) {
        map[contextKey] = next;
    } else {
        delete map[contextKey];
    }

    writeStorage(map);
}

export function resetStoredColorSettings(context?: ColorSettingsContext | number | null): void {
    const map = readStorage();
    const contextKey = getPrimaryKey(context);
    if (map[contextKey]) {
        delete map[contextKey];
    }

    writeStorage(map);
}
